import React, { useEffect, useMemo, useState } from "react"
import { Sparkles, Plus, X, RotateCcw } from "lucide-react"

import { funEU } from "../../../utils/firebase"

import Button from "../../../components/ui/button/button"
import Checkbox from "../../../components/ui/checkbox/checkbox"
import Input from "../../../components/ui/inputs/input"
import Textarea from "../../../components/ui/inputs/textarea"
import Select from "../../../components/ui/select/select"

import {
	AI_MODELS,
	DEFAULT_FIELDS,
	DEFAULT_MODEL,
	DEFAULT_PREDICTIONS,
	DEFAULT_SYSTEM_INSTRUCTIONS,
	DEFAULT_TEMPERATURE,
	DEFAULT_USER_INSTRUCTIONS,
	FIELD_TYPES,
	PLACEHOLDER_VARIABLES,
	buildPreviewFromFields,
	buildSchemaFromFields,
	localStorageKey,
} from "../registry"

import {
	looksLikeHtml,
	readLocalStorage,
	validateSettings,
	writeLocalStorage,
} from "./storage"
import { ResultDisplay } from "./result-display"

/**
 * Development tab — the original Enquiry Ingest authoring surface. All
 * prompt/field/prediction state is persisted to localStorage so it survives
 * refreshes AND can be picked up by the queue tab's parse modal via
 * `loadSettings()`. This tab is the canonical writer of those keys.
 */
function DevelopmentTab() {
	const [model, setModel] = useState(() =>
		readLocalStorage(localStorageKey("model"), DEFAULT_MODEL)
	)
	const [temperature, setTemperature] = useState(() =>
		String(readLocalStorage(localStorageKey("temperature"), DEFAULT_TEMPERATURE))
	)
	const [systemInstructions, setSystemInstructions] = useState(() =>
		readLocalStorage(localStorageKey("system"), DEFAULT_SYSTEM_INSTRUCTIONS)
	)
	const [userInstructions, setUserInstructions] = useState(() =>
		readLocalStorage(localStorageKey("user"), DEFAULT_USER_INSTRUCTIONS)
	)
	const [fields, setFields] = useState(() =>
		readLocalStorage(localStorageKey("fields"), DEFAULT_FIELDS)
	)
	const [predictions, setPredictions] = useState(() =>
		readLocalStorage(localStorageKey("predictions"), DEFAULT_PREDICTIONS)
	)

	const [body, setBody] = useState(() => readLocalStorage(localStorageKey("body"), ""))
	const [subject, setSubject] = useState(() =>
		readLocalStorage(localStorageKey("subject"), "")
	)
	const [fromName, setFromName] = useState(() =>
		readLocalStorage(localStorageKey("from_name"), "")
	)
	const [fromAddress, setFromAddress] = useState(() =>
		readLocalStorage(localStorageKey("from_address"), "")
	)

	useEffect(() => writeLocalStorage(localStorageKey("model"), model), [model])
	useEffect(
		() => writeLocalStorage(localStorageKey("temperature"), temperature),
		[temperature]
	)
	useEffect(
		() => writeLocalStorage(localStorageKey("system"), systemInstructions),
		[systemInstructions]
	)
	useEffect(
		() => writeLocalStorage(localStorageKey("user"), userInstructions),
		[userInstructions]
	)
	useEffect(() => writeLocalStorage(localStorageKey("fields"), fields), [fields])
	useEffect(
		() => writeLocalStorage(localStorageKey("predictions"), predictions),
		[predictions]
	)
	useEffect(() => writeLocalStorage(localStorageKey("body"), body), [body])
	useEffect(() => writeLocalStorage(localStorageKey("subject"), subject), [subject])
	useEffect(() => writeLocalStorage(localStorageKey("from_name"), fromName), [fromName])
	useEffect(
		() => writeLocalStorage(localStorageKey("from_address"), fromAddress),
		[fromAddress]
	)

	const [running, setRunning] = useState(false)
	const [error, setError] = useState(null)
	const [result, setResult] = useState(null)

	const modelOptions = useMemo(
		() => Object.fromEntries(AI_MODELS.map((m) => [m, m])),
		[]
	)
	const typeOptions = useMemo(
		() => Object.fromEntries(FIELD_TYPES.map((t) => [t, t])),
		[]
	)

	const resetToDefaults = () => {
		if (!window.confirm("Reset all settings (prompts, fields, model) to defaults?")) return
		setModel(DEFAULT_MODEL)
		setTemperature(String(DEFAULT_TEMPERATURE))
		setSystemInstructions(DEFAULT_SYSTEM_INSTRUCTIONS)
		setUserInstructions(DEFAULT_USER_INSTRUCTIONS)
		setFields(DEFAULT_FIELDS)
		setPredictions(DEFAULT_PREDICTIONS)
	}

	const updatePrediction = (index, patch) => {
		setPredictions((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)))
	}
	const removePrediction = (index) =>
		setPredictions((prev) => prev.filter((_, i) => i !== index))
	const addPrediction = () =>
		setPredictions((prev) => [...prev, { key: "", label: "", description: "" }])

	const updateField = (index, patch) => {
		setFields((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)))
	}
	const removeField = (index) =>
		setFields((prev) => prev.filter((_, i) => i !== index))
	const addField = () =>
		setFields((prev) => [
			...prev,
			{ key: "", label: "", type: "string", description: "", required: false },
		])

	const previewObject = useMemo(
		() => buildPreviewFromFields(fields, predictions),
		[fields, predictions]
	)

	const run = async () => {
		setRunning(true)
		setError(null)
		setResult(null)

		try {
			validateSettings({ fields, predictions })
			if (!body.trim()) {
				throw new Error("Paste an email body or text into the input above")
			}

			const schema = buildSchemaFromFields(fields, predictions)
			const callable = funEU.httpsCallable("runEmailAiTool")
			const response = await callable({
				inline_email: {
					body,
					body_content_type: looksLikeHtml(body) ? "html" : "text",
					subject,
					from_name: fromName,
					from_address: fromAddress,
					occurred_at: new Date().toISOString(),
				},
				model,
				temperature: Number(temperature) || 0,
				system_instructions: systemInstructions,
				user_instructions: userInstructions,
				response_format: "json_schema",
				json_schema: schema,
				json_schema_name: "enquiry_ingest",
				json_schema_strict: true,
				custom_variables: {},
				pass_images: false,
			})
			setResult(response.data || null)
		} catch (err) {
			setError(err?.message || String(err))
		} finally {
			setRunning(false)
		}
	}

	return (
		<>
			<div className="enquiry-ingest-tab-actions">
				<Button
					className="OUTLINE"
					small={true}
					label="Reset to defaults"
					icon={<RotateCcw size={14} />}
					onClick={resetToDefaults}
				/>
			</div>

			<div className="enquiry-ingest-layout">
				{/* Left column: prompt + parser configuration */}
				<div className="enquiry-ingest-column">
					<section className="ai-panel is-open">
						<div className="ai-panel-toggle">
							<Sparkles size={14} />
							<span>Model & prompt</span>
						</div>
						<div className="ai-panel-body">
							<div className="ai-panel-grid">
								<div className="ai-panel-field">
									<Select
										showLabel
										placeholder="Model:"
										value={model}
										selected={model}
										onSelect={(option) => setModel(option.option)}
										options={modelOptions}
									/>
								</div>
								<div className="ai-panel-field">
									<Input
										type="text"
										placeholder="Temperature (0–2):"
										value={temperature}
										onChange={setTemperature}
										showLabel
									/>
								</div>
							</div>
							<div className="ai-panel-field">
								<Textarea
									placeholder="System instructions"
									value={systemInstructions}
									onChange={setSystemInstructions}
									showLabel
									rows={10}
								/>
							</div>
							<div className="ai-panel-field">
								<Textarea
									placeholder="User instructions"
									value={userInstructions}
									onChange={setUserInstructions}
									showLabel
									rows={12}
								/>
								<small className="ai-panel-helper">
									Available placeholders:{" "}
									{PLACEHOLDER_VARIABLES.map((v) => (
										<code key={v.key}>{`{{${v.key}}}`}</code>
									))}
								</small>
							</div>
						</div>
					</section>

					<section className="ai-panel is-open">
						<div className="ai-panel-toggle">
							<Sparkles size={14} />
							<span>Fields to extract ({fields.length})</span>
						</div>
						<div className="ai-panel-body">
							<p className="ai-panel-description">
								Each field becomes a property in the JSON schema sent to the
								model. Required fields must always be filled; optional fields
								are nullable. Keys must be lowercase snake_case.
							</p>
							<div className="ingest-fields">
								{fields.map((field, index) => (
									<FieldRow
										key={index}
										field={field}
										typeOptions={typeOptions}
										onChange={(patch) => updateField(index, patch)}
										onRemove={() => removeField(index)}
									/>
								))}
								<button
									type="button"
									className="ai-panel-list-add"
									onClick={addField}>
									<Plus size={14} /> Add field
								</button>
							</div>
						</div>
					</section>

					<section className="ai-panel is-open">
						<div className="ai-panel-toggle">
							<Sparkles size={14} />
							<span>Predictions ({predictions.length})</span>
						</div>
						<div className="ai-panel-body">
							<p className="ai-panel-description">
								Triage signals scored 0–1 by the model. Each one becomes a
								property on a nested <code>predictions</code> object in the
								response. Use these for routing, sorting, or surfacing leads —
								not for things you could derive from the structured fields.
							</p>
							<div className="ingest-fields">
								{predictions.map((prediction, index) => (
									<PredictionRow
										key={index}
										prediction={prediction}
										onChange={(patch) => updatePrediction(index, patch)}
										onRemove={() => removePrediction(index)}
									/>
								))}
								<button
									type="button"
									className="ai-panel-list-add"
									onClick={addPrediction}>
									<Plus size={14} /> Add prediction
								</button>
							</div>
						</div>
					</section>
				</div>

				{/* Right column: input + run + result */}
				<div className="enquiry-ingest-column">
					<section className="ai-panel is-open">
						<div className="ai-panel-toggle">
							<Sparkles size={14} />
							<span>Email / text to parse</span>
						</div>
						<div className="ai-panel-body">
							<p className="ai-panel-description">
								All three header fields are optional — paste any text into the
								body below and hit Parse. The headers are only useful when the
								model needs them as additional context (e.g. sender domain).
							</p>
							<div className="ai-panel-grid two-cols">
								<div className="ai-panel-field">
									<Input
										type="text"
										placeholder="From name (optional)"
										value={fromName}
										onChange={setFromName}
										showLabel
									/>
								</div>
								<div className="ai-panel-field">
									<Input
										type="text"
										placeholder="From address (optional)"
										value={fromAddress}
										onChange={setFromAddress}
										showLabel
									/>
								</div>
							</div>
							<div className="ai-panel-field">
								<Input
									type="text"
									placeholder="Subject (optional)"
									value={subject}
									onChange={setSubject}
									showLabel
								/>
							</div>
							<div className="ai-panel-field">
								<Textarea
									placeholder="Email body or pasted text"
									value={body}
									onChange={setBody}
									showLabel
									rows={18}
								/>
								<small className="ai-panel-helper">
									HTML is auto-detected and stripped to plain text before being
									substituted into <code>{`{{body_text}}`}</code>.
								</small>
							</div>
							<div className="ai-panel-actions">
								<Button
									label="Parse"
									icon={<Sparkles size={14} />}
									loading={running}
									loadingText="Parsing..."
									onClick={run}
									disabled={running || !body.trim()}
								/>
							</div>
							{error && (
								<div
									className="ai-panel-error"
									role="alert">
									{error}
								</div>
							)}
						</div>
					</section>

					<section className="ai-panel is-open">
						<div className="ai-panel-toggle">
							<Sparkles size={14} />
							<span>Expected JSON shape</span>
						</div>
						<div className="ai-panel-body">
							<p className="ai-panel-description">
								Live preview of the JSON shape the model is being asked to
								return, built from the fields above. Optional fields show as
								<code> null</code>; required fields show a placeholder of the
								correct type.
							</p>
							<pre className="ai-panel-result-json full-height">
								{JSON.stringify(previewObject, null, 2)}
							</pre>
						</div>
					</section>

					{result && (
						<ResultDisplay
							result={result}
							fields={fields}
							predictions={predictions}
						/>
					)}
				</div>
			</div>
		</>
	)
}

function FieldRow({ field, typeOptions, onChange, onRemove }) {
	return (
		<div className="ingest-field-row">
			<div className="ingest-field-row-grid">
				<Input
					type="text"
					placeholder="key (snake_case)"
					value={field.key}
					onChange={(next) => onChange({ key: next })}
					showLabel
				/>
				<Input
					type="text"
					placeholder="Label"
					value={field.label}
					onChange={(next) => onChange({ label: next })}
					showLabel
				/>
				<Select
					showLabel
					placeholder="Type:"
					value={field.type}
					selected={field.type}
					onSelect={(option) => onChange({ type: option.option })}
					options={typeOptions}
				/>
				<div className="ingest-field-row-trailing">
					<Checkbox
						label="Required"
						checked={!!field.required}
						onClick={() => onChange({ required: !field.required })}
					/>
					<button
						type="button"
						className="ingest-field-button danger"
						onClick={onRemove}
						aria-label="Remove field">
						<X size={14} />
					</button>
				</div>
			</div>
			<Textarea
				placeholder="Description (sent to the model)"
				value={field.description}
				onChange={(next) => onChange({ description: next })}
				showLabel
				rows={2}
			/>
		</div>
	)
}

function PredictionRow({ prediction, onChange, onRemove }) {
	return (
		<div className="ingest-field-row">
			<div className="ingest-prediction-row-grid">
				<Input
					type="text"
					placeholder="key (snake_case)"
					value={prediction.key}
					onChange={(next) => onChange({ key: next })}
					showLabel
				/>
				<Input
					type="text"
					placeholder="Label"
					value={prediction.label}
					onChange={(next) => onChange({ label: next })}
					showLabel
				/>
				<button
					type="button"
					className="ingest-field-button danger"
					onClick={onRemove}
					aria-label="Remove prediction">
					<X size={14} />
				</button>
			</div>
			<Textarea
				placeholder="Description (sent to the model — calibrate the 0–1 scale)"
				value={prediction.description}
				onChange={(next) => onChange({ description: next })}
				showLabel
				rows={2}
			/>
		</div>
	)
}

export default DevelopmentTab
